import {
  FilterRowSelection,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';

/**
 * Applies the relational operator to filter the values based on the specified operator.
 *
 * @param filterRowValue - The relational operator and value to filter by.
 * @param selectedValues - The values to filter.
 * @param dataType - The data type of the values ('number', 'string', or 'boolean').
 * @returns An array of values that satisfy the relational operator condition.
 */
export const applyRelationalOperator = (
  filterRowValue: FilterRowSelection,
  selectedValues: IterableIterator<string | null>,
  dataType: string // 'number', 'string', or 'boolean'
) => {
  if (dataType === 'boolean') {
    // For booleans, interpret the user input and each item as a boolean.
    const rhsValue =
      filterRowValue.value[0] === true || filterRowValue.value[0] === 'true';
    return Array.from(selectedValues).filter(item => {
      // Handle null values explicitly - they are neither true nor false
      if (item === null || item === undefined) {
        switch (filterRowValue.relationalOperator) {
          case RelationalOperator.NOT_EQUAL:
            // null is considered "not equal" to any specific boolean value
            return true;
          default:
            return false;
        }
      }

      const lhsValue = item === 'true';
      switch (filterRowValue.relationalOperator) {
        case RelationalOperator.NOT_EQUAL:
          return lhsValue !== rhsValue;
        // Add additional boolean cases if needed
        default:
          return false;
      }
    });
  } else if (dataType === 'number') {
    const rhsUserInputNumber = Number(filterRowValue.value[0]);
    return Array.from(selectedValues).filter(item => {
      const lhsSelectionValue = Number(item);
      switch (filterRowValue.relationalOperator) {
        case RelationalOperator.NOT_EQUAL:
          return lhsSelectionValue !== rhsUserInputNumber;
        case RelationalOperator.LESS_THAN_OR_EQUAL:
          return lhsSelectionValue <= rhsUserInputNumber;
        case RelationalOperator.GREATER_THAN_OR_EQUAL:
          return lhsSelectionValue >= rhsUserInputNumber;
        case RelationalOperator.LESS_THAN:
          return lhsSelectionValue < rhsUserInputNumber;
        case RelationalOperator.GREATER_THAN:
          return lhsSelectionValue > rhsUserInputNumber;
        default:
          return false;
      }
    });
  } else if (dataType === 'string') {
    // For strings, use direct comparison.
    const rhsValue = filterRowValue.value[0]?.toString();
    return Array.from(selectedValues).filter(item => {
      switch (filterRowValue.relationalOperator) {
        case RelationalOperator.NOT_EQUAL:
          return item !== rhsValue;
        // You can add more string operators if necessary.
        default:
          return false;
      }
    });
  }
  return [];
};
